import { defineStore } from 'pinia';
import { useFormStore } from '../form/formStore';
import { UpgradeRoadsideService as _upgradeRoadsideService } from '@/services/store/upgrade-roadside.service';
import { RoadsideAssistanceService as _roadsideAssistanceService } from '@/services/store/roadside-assistance.service';
import { computed, ref } from 'vue';
import { UpgradeRoadsideDetails } from '@/interfaces/self-service/upgrade-roadside.interface';
import { RoadsideVehicle } from '@/interfaces/entities/roadside-vehicle.interface';
import { FinaliseQuoteResponse } from '@/interfaces/entities/finalise-quote.interface';
import { FormEvents } from '@/enums/form-events.enum';
import {
  IAnalyticsForm,
  IAnalyticsProfileInfo,
  IAnalyticsPaymentDetail,
  IFormAnalytics,
} from '@/interfaces/forms/analytics-interface';
import { useFindCarStore } from '../find-car/find-car.state';
import { useAccountStore } from '../account/account.state';
import { useAnalyticsStore } from '../analytics/analytics.state';
import { UpgradeRoadsideAssistanceProductResponse } from '@/interfaces/entities/ur-upgrade-rsa-product.interface';
import useVehicleEligibility from '@/composables/useVehicleEligibility';
import { UpgradeRoadsideConfirmationEmailRequest } from '@/interfaces/entities/upgrade-roadside-confirmation-email-request.interface';
import { mapVehicle } from '@/utils/review-summary-mapping-util';
import { getRoadsideProductNameFromProductCode } from '@/utils/roadside-helpers';
import { HOME_PAGE } from '@/constants/urls.const';
import { CardTypes } from '@/enums/payment-types.enum';

export const useUpgradeRoadsideStore = defineStore('UpgradeRoadside', () => {
  const formStore = useFormStore();
  const findCarStore = useFindCarStore();
  const accountStore = useAccountStore();
  const analyticsStore = useAnalyticsStore();

  // ------------------------------------------------------------- //
  // Define State
  // ------------------------------------------------------------- //
  function $reset() {}

  const upgradeRoadsideDetails = ref<UpgradeRoadsideDetails>({
    policyId: '',
    currentRsaProduct: '',
    shouldUpdateVehicle: undefined,
    vehicleInSoundCondition: undefined,
    vehicle: undefined,
    newProductCode: '',
    oldProductCode: '',
    upgradeQuotes: null,
    policyStatus: null,
  });

  const creditCardDetails = ref<Record<string, string>>({});
  const paymentReceipt = ref<{
    result: string;
    transactionId: string;
    reference: string;
  }>();

  const finaliseQuoteResponse = ref<FinaliseQuoteResponse | null>(null); // Response from the finaliseQuote call
  const isDirectDebit = ref(false); // Whether the payment is direct debit
  const isAutoPayment = ref(false); // Whether direct debit is already on
  const isQuoteFinalised = ref(false); // Whether a quote has been finalised from the entered details
  const paymentAttemptCount = ref(0); // Tracks the number of attempts made to pay for the quote
  const finaliseQuoteAttemptCount = ref(0); // Tracks the number of attempts made to finalise the quote
  const originalCoverEndDate = ref<Date | null>(null);
  const currentPolicyVehicleId = ref(''); // VehicleId for the current policy
  const selectedUpgradeRsaProduct = computed(() => {
    // Returns the selected RSA cover to upgrade to
    if (upgradeRoadsideDetails.value.newProductCode) {
      const selectedUpgradeRsaProduct = upgradeRoadsideDetails.value.upgradeQuotes?.find(
        (x) => x.productCode === upgradeRoadsideDetails.value.newProductCode
      );
      return selectedUpgradeRsaProduct;
    }
    return null;
  });

  const { vehicleEligibility, setVehicleEligibility } = useVehicleEligibility();

  function setVehicleDetails(roadsideVehicle: RoadsideVehicle) {
    upgradeRoadsideDetails.value.vehicle = roadsideVehicle;
  }

  function clearVehicleDetails() {
    upgradeRoadsideDetails.value.vehicle = undefined;
  }

  function mapPaymentDetails(formData: Record<string, any>) {
    creditCardDetails.value = {
      cardToken: formData.cardToken,
      cardHolder: formData.cardHolder,
      maskedCardNumber: formData.MaskedCardNumber,
      cardExpiry: formData.cardExpiry,
      cardType: formData.cardType,
      autoRenew: formData.autoRenew,
    };

    // Set direct debit state
    if (formData.autoRenew?.toLowerCase() === 'true') {
      isDirectDebit.value = true;
    }
  }

  //#region Quote
  function invalidateQuoteFinalising() {
    // Clear the fields related to a finalised quote
    finaliseQuoteResponse.value = null;
    isQuoteFinalised.value = false;
  }

  async function finaliseQuote(): Promise<boolean> {
    try {
      if (!selectedUpgradeRsaProduct.value?.apttusQuoteId || !selectedUpgradeRsaProduct.value?.apttusCartId) {
        throw new Error('MISSING_PARAMS');
      }
      const response: FinaliseQuoteResponse | null = await _roadsideAssistanceService.finaliseQuote(
        selectedUpgradeRsaProduct.value?.apttusQuoteId ?? '',
        selectedUpgradeRsaProduct.value?.apttusCartId ?? ''
      );
      if (!response || response.isError) {
        // Failure
        invalidateQuoteFinalising();
        throw new Error('API_FAILED');
      }

      if (!response.isError) {
        // Success
        finaliseQuoteResponse.value = response;
        isQuoteFinalised.value = true;
        return true;
      }

      throw new Error('MISSING_PARAMS');
    } catch (error) {
      console.error(error);
      return false;
    }
  }
  //#endregion

  async function submitPurchase() {
    try {
      const basePayload = {
        prn: finaliseQuoteResponse.value?.prn,
        amount: selectedUpgradeRsaProduct.value?.price,
        invoiceId: finaliseQuoteResponse.value?.invoiceLineItem.invoiceId,
        apttusId: accountStore.identityDetails?.apttusId,
      };

      const isWalletPayment = [CardTypes.GooglePay, CardTypes.ApplePay].includes(
        creditCardDetails.value.cardType as CardTypes
      );

      const payload = isWalletPayment
        ? {
            ...basePayload,
            walletToken: creditCardDetails.value.cardToken,
            walletProvider: creditCardDetails.value.cardType,
          }
        : {
            ...basePayload,
            cardToken: creditCardDetails.value.cardToken,
            // AutoRenew Exclusive Fields
            autoRenew: creditCardDetails.value.autoRenew,
            cardHolder: creditCardDetails.value.cardHolder,
            MaskedCardNumber: creditCardDetails.value.maskedCardNumber,
            cardExpiry: creditCardDetails.value.cardExpiry,
            cardType: creditCardDetails.value.cardType,
            assetId: finaliseQuoteResponse.value?.assetId,
          };

      const result = await _roadsideAssistanceService.submitPurchase(payload);
      paymentReceipt.value = result;
      return result;
    } catch {
      return null;
    }
  }

  async function upgradeRoadsideVehicle() {
    try {
      const vehicle = findCarStore.selectedVehicle;
      const result = await _roadsideAssistanceService.upgradeRoadsideVehicle({
        vehicle: {
          id: currentPolicyVehicleId.value,
          nvic: vehicle?.nvic,
          registrationNumber: upgradeRoadsideDetails.value.vehicle?.vehicleRegistration,
          stateCode: vehicle?.stateCode,
          make: { description: vehicle!.make.description, code: vehicle!.make.code },
          model: { description: vehicle!.model.description, code: vehicle!.model.code },
          body: {
            description: vehicle!.body.description,
            colour: findCarStore.vehicleColour || vehicle!.colour,
            code: vehicle!.body.code,
          },
          manufacturingYear: vehicle?.manufacturingYear,
          variant: { description: vehicle!.variant.description, code: vehicle!.variant.code },
          fuelType: { code: vehicle!.fuelType.code },
          kerbWeight: vehicle!.kerbWeight,
          kerbWeightUnit: vehicle!.kerbWeightUnit,
          length: vehicle!.length,
          lengthUnit: vehicle!.lengthUnit,
          loadCapacity: vehicle!.loadCapacity,
          loadCapacityUnit: vehicle!.loadCapacityUnit,
          option: { transmission: vehicle!.option.transmission, series: '', doors: '' },
        },
        apttusId: accountStore.identityDetails!.apttusId!,
        policyId: upgradeRoadsideDetails.value.policyId,
      });
      return result;
    } catch (error) {
      console.error('Failed to upgrade roadside for vehicle', error);
      return null;
    }
  }

  function pushAnalytics(options: {
    event: FormEvents;
    form: IAnalyticsForm;
    profileInfo?: IAnalyticsProfileInfo;
    payment?: Partial<IAnalyticsPaymentDetail>;
  }) {
    const model: IFormAnalytics = {
      event: options.event,
      form: options.form,
    };

    if (!options.form.termEndDate && originalCoverEndDate.value) {
      options.form.termEndDate = originalCoverEndDate.value.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    }

    if (!options.form.detail && findCarStore.selectedVehicle) {
      options.form.detail = {
        bodyType: findCarStore.selectedVehicle.body.code,
        makeName: findCarStore.selectedVehicle.make.description,
        modelName: findCarStore.selectedVehicle.model.description,
        vehicleYear: findCarStore.selectedVehicle.manufacturingYear.toString(),
      };
    } else if (!options.form.detail && upgradeRoadsideDetails.value.vehicle) {
      options.form.detail = {
        bodyType: upgradeRoadsideDetails.value.vehicle.vehicleBody,
        makeName: upgradeRoadsideDetails.value.vehicle.vehicleMake,
        modelName: upgradeRoadsideDetails.value.vehicle.vehicleModel,
        vehicleYear: upgradeRoadsideDetails.value.vehicle.vehicleYear,
      };
    }

    if (options.payment) {
      model.payment = options.payment;
    }
    model.form.name = formStore.formName;

    analyticsStore.pushAnalytics(model);
  }

  const setPolicyStatus = async (policyStatus: string | null): Promise<void> => {
    try {
      upgradeRoadsideDetails.value.policyStatus = policyStatus;
    } catch (error) {
      console.error('Failed to set policy status:', error);
    }
  };

  const setEligibleProductForUpgrade = async (
    policyId: string
  ): Promise<{ status: boolean | null; policyStatus?: string }> => {
    try {
      const apptusId = accountStore?.identityDetails?.apttusId;

      if (!policyId || !apptusId) {
        return { status: null };
      }

      const result = await _roadsideAssistanceService.getEligibleProductForUpgrade(apptusId, policyId);

      if (result?.isError) {
        return { status: false, policyStatus: result?.policyStatus };
      } else if (result?.vehicle !== null && result?.vehicle.make && result?.vehicle.model) {
        upgradeRoadsideDetails.value.policyId = policyId;
        upgradeRoadsideDetails.value.currentRsaProduct = result?.tierId;
        upgradeRoadsideDetails.value.oldProductCode = result?.productCode;
        isAutoPayment.value = result?.isAutoPayment;
        originalCoverEndDate.value = result.endDate;

        // Store the vehicleId for the vehicle associated with the current policy
        currentPolicyVehicleId.value = result.vehicle.id;

        setVehicleDetails({
          vehicleRegistration: result?.vehicle.registrationNumber,
          vehicleBody: result?.vehicle.body,
          vehicleMake: result?.vehicle.make,
          vehicleModel: result?.vehicle.model,
          vehicleYear: result?.vehicle.year,
          vehicleColour: result?.vehicle.colour,
          vehicleVariant: result?.vehicle.variant,
          vehicleGlassesId: result?.vehicle.glassesId,
        } as RoadsideVehicle);

        const hasUpgradeQuotes = await setUpgradeQuotes(result);
        if (!hasUpgradeQuotes) {
          return { status: null, policyStatus: result?.policyStatus };
        }

        return { status: true, policyStatus: result?.policyStatus };
      }

      return { status: null, policyStatus: result?.policyStatus };
    } catch {
      return { status: false };
    }
  };

  const setUpgradeQuotes = async (upgradeRsaProduct: UpgradeRoadsideAssistanceProductResponse): Promise<boolean> => {
    try {
      if (upgradeRsaProduct) {
        const result = await _roadsideAssistanceService.getUpgradeQuotes(
          accountStore?.identityDetails?.apttusId ?? '',
          upgradeRsaProduct.productCode,
          upgradeRsaProduct.price,
          upgradeRsaProduct.assetLineItemId
        );

        if (result) {
          // Sort results in ascending order by price
          result.sort((a, b) => {
            if (a.price === null && b.price === null) return 0;
            if (a.price === null) return 1; // null goes after valid number
            if (b.price === null) return -1; // valid number goes before null
            return a.price - b.price; // bothe are numbers
          });
          upgradeRoadsideDetails.value.upgradeQuotes = result;
          return true;
        }
      }
      return false;
    } catch {
      return false;
    }
  };

  /**
   * Back to self service roadside assistance
   */
  async function redirectToSelfServiceRoadsideAssistance() {
    try {
      const url = await _upgradeRoadsideService.getSelfServiceRoadsideAssistancePage();
      window.location.href = url;
    } catch (error) {
      console.warn('Unable to retrieve self service roadside assistance URL: ', error);
      // Session timeout
      window.location.href = HOME_PAGE;
    }
  }

  async function sendEmail() {
    const vehicle = mapVehicle(
      upgradeRoadsideDetails.value.vehicle?.vehicleMake,
      upgradeRoadsideDetails.value.vehicle?.vehicleModel,
      upgradeRoadsideDetails.value.vehicle?.vehicleYear,
      upgradeRoadsideDetails.value.vehicle?.vehicleColour
    );

    const emailRequest: UpgradeRoadsideConfirmationEmailRequest = {
      receiptNumber: finaliseQuoteResponse.value?.prn,
      amount: selectedUpgradeRsaProduct.value!.price!,
      product: selectedUpgradeRsaProduct.value!.productName + '*',
      paymentMethod: creditCardDetails.value.cardType,
      oldProduct: getRoadsideProductNameFromProductCode(upgradeRoadsideDetails.value.oldProductCode!),
      newProduct: getRoadsideProductNameFromProductCode(upgradeRoadsideDetails.value.newProductCode!),
      d365Id: accountStore?.identityDetails?.d365Id,
      isVehicleUpdated: upgradeRoadsideDetails.value.shouldUpdateVehicle === true,
      vehicle,
    };

    await _roadsideAssistanceService.sendUpgradeRoadsideEmail(emailRequest);
  }

  return {
    upgradeRoadsideDetails,
    isDirectDebit,
    isQuoteFinalised,
    isAutoPayment,
    finaliseQuoteAttemptCount,
    paymentAttemptCount,
    creditCardDetails,
    finaliseQuoteResponse,
    originalCoverEndDate,
    selectedUpgradeRsaProduct,
    setVehicleDetails,
    clearVehicleDetails,
    mapPaymentDetails,
    finaliseQuote,
    submitPurchase,
    pushAnalytics,
    setPolicyStatus,
    setEligibleProductForUpgrade,
    upgradeRoadsideVehicle,
    setUpgradeQuotes,
    redirectToSelfServiceRoadsideAssistance,
    vehicleEligibility,
    setVehicleEligibility,
    sendEmail,
  };
});
